import { observer } from 'mobx-react';
import { useRootStore } from '@nandosaus/state-management';
import PropTypes from 'prop-types';
import React from 'react';
import { ORDER_TYPES } from '@nandosaus/constants';

import { OrderItemList } from '@components/order-item-list';
import { OrderTotals } from '@components/order-totals';
import { OrderDetailsSummaryTemplate } from '@components/order-summary/order-details-summary-template';
import { Box, Flex } from '../grid';
import { P, Label } from '../typography/new';
import { Modal } from '../modal/new';
import { Button } from '../button/new';

const ReorderModal = ({
  modalOpen,
  handleModalClose,
  orderId,
  handleReorder,
  catering,
  order: orderProp,
  canReorder,
  startEditOrder,
  handleCancelOrder,
  isCateringCutoffPassed,
  isOrderInactive,
  isAccountPage,
}) => {
  const { PastOrdersStore } = useRootStore();

  const { getCateringOrderById, getOrderById } = PastOrdersStore;
  const order = orderProp || (catering ? getCateringOrderById(orderId) : getOrderById(orderId));

  if (!order) {
    return null;
  }

  // Use different properties based on order type
  const orderDetails = {
    pickupTime: order.formattedPickupTime,
    restaurantAddress: order.formattedRestaurantAddress,
    restaurant: order.restaurantDetails,
    deliveryStreet: order.deliveryAddress?.formattedStreet,
    deliverySuburbStatePostcodeCountry: order.deliveryAddress?.formattedSuburbStatePostcodeCountry,
    headcount: order?.cateringHeadcount,
    pricePerPerson: order?.formattedPricePerPerson,
  };

  const {
    formattedOrderItems,
    defaultFormattedOrderItems,
    formattedSubtotal,
    defaultCateringFormattedOrderItems,
    formattedDeliveryFee,
  } = order;

  // Catering past orders don't carry a reliable orderType from the BE, so the catering surface
  // defaults to CATERING (pickup) — but a catering DELIVERY order must keep its delivery type so
  // the summary template renders the delivery icon + address rather than the pickup layout.
  const resolvedOrderType = catering
    ? (order.isCateringDeliveryOrder && ORDER_TYPES.CATERING_DELIVERY) || ORDER_TYPES.CATERING
    : order.orderType;

  // On the account page, MenuStore.menu may not be loaded, so use defaultFormattedOrderItems.
  const getResolvedOrderItems = () => {
    if (catering) return defaultCateringFormattedOrderItems;
    if (isAccountPage) return defaultFormattedOrderItems;
    return formattedOrderItems;
  };
  const resolvedOrderItems = getResolvedOrderItems();

  const closeModal = () => {
    handleModalClose();
  };

  if (!orderId) {
    return null;
  }

  const transformOrderItemsForList = orderItems => {
    if (!orderItems || orderItems.length === 0) {
      return [];
    }

    return orderItems.map(item => ({
      ...item,
      choices: item.productChoices || [],
      quantity: item.productQuantity,
      totals: { formattedPrice: item.price },
    }));
  };

  return (
    <Modal
      handleClose={closeModal}
      open={modalOpen}
      width={{ _: 'calc(100% - 34px)', md: '560px' }}
      modalTitle="Order Details"
      hideBottomDecal={false}
      height="fit-content"
      borderRadius="24px"
      maxHeight={{ _: '90%', md: '85%' }}
    >
      <Box position="relative">
        <Box mt="16px">
          {catering && (
            <Flex flexDirection="column" gap="2px" mb="20px">
              <Label textTransform="uppercase">Order number</Label>
              <P variant="small">{order.id?.length > 10 ? 'coming soon' : order.id}</P>
            </Flex>
          )}
          <OrderDetailsSummaryTemplate
            orderType={resolvedOrderType}
            deliveryUrl={order?.deliveryTrackingUrl}
            locationIconOverride={order.isCateringDeliveryOrder ? 'locationMarker' : null}
            location={
              order.isCateringDeliveryOrder
                ? {
                    streetDetails: orderDetails.deliveryStreet,
                    suburbDetails: orderDetails.deliverySuburbStatePostcodeCountry,
                  }
                : { streetDetails: orderDetails.restaurant?.name, suburbDetails: orderDetails.restaurantAddress }
            }
            orderDetail={orderDetails.pickupTime}
            error={null}
            isCheckout
          />
          <Flex height="4px" width="200%" backgroundColor="warmWhite3" mx="-50px" mt="32px" mb="20px" />
          <Box>
            <P
              role="heading"
              aria-level="3"
              pb="12px"
              borderBottom="1px solid"
              borderColor="warmWhite3"
              variant="small-semi-bold"
            >
              {resolvedOrderItems.length} items
            </P>
            <OrderItemList orderItems={transformOrderItemsForList(resolvedOrderItems)} disableOnClick />
          </Box>
          <Box>
            <OrderTotals
              totalPrices={{ formattedPrice: order.formattedTotalAfterPoints }}
              subtotalPrices={{ formattedPrice: formattedSubtotal }}
              surcharges={order.formattedSurcharges}
              deliveryCost={formattedDeliveryFee}
              deliveryLabelColor={null}
              pricePerPerson={orderDetails.pricePerPerson}
              cateringHeadcount={orderDetails.headcount}
              pointsSpent={order.pointsSpent}
              pointsAmountFormatted={order.pointsSpentAmountFormatted}
              borderTop
              showDisclaimer={false}
            />
          </Box>
          <Box>
            <P variant="extra-small" mb="20px" color="grey70" textAlign="center">
              Kilojoule calculations do not include drinks or customisations to menu items. The average adult daily
              energy intake is 8,700 kJ.
            </P>
            {canReorder && (
              <>
                <Flex gap="12px">
                  <Button
                    isFullWidth
                    size="tall"
                    widthType="full"
                    variant={
                      catering && !isCateringCutoffPassed && !isOrderInactive && isAccountPage ? 'secondary' : 'primary'
                    }
                    onClick={() => {
                      closeModal();
                      handleReorder();
                    }}
                  >
                    Reorder
                  </Button>

                  {catering && !isCateringCutoffPassed && !isOrderInactive && isAccountPage && (
                    <Button
                      isFullWidth
                      size="tall"
                      variant="primary"
                      widthType="full"
                      onClick={() => {
                        closeModal();
                        if (startEditOrder) startEditOrder();
                      }}
                    >
                      Edit Order
                    </Button>
                  )}
                </Flex>

                <P mt="20px" mb="20px" variant="small" textAlign="center">
                  You can always make changes before submitting your order
                </P>

                {catering && !isCateringCutoffPassed && !isOrderInactive && isAccountPage && (
                  <Button
                    isFullWidth
                    size="tall"
                    widthType="full"
                    textDecoration="underline"
                    mb="24px"
                    style={{ textDecoration: 'underline' }}
                    variant="link"
                    onClick={() => {
                      closeModal();
                      if (handleCancelOrder) handleCancelOrder();
                    }}
                  >
                    Cancel Order
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

ReorderModal.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  handleModalClose: PropTypes.func.isRequired,
  handleReorder: PropTypes.func,
  orderId: PropTypes.string,
  catering: PropTypes.bool,
  order: PropTypes.object,
  content: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    subheading: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    disclaimer: PropTypes.string.isRequired,
    cta: PropTypes.string.isRequired,
  }).isRequired,
  canReorder: PropTypes.bool,
  startEditOrder: PropTypes.func,
  handleCancelOrder: PropTypes.func,
  isCateringCutoffPassed: PropTypes.bool,
  isOrderInactive: PropTypes.bool,
  isAccountPage: PropTypes.bool,
};

ReorderModal.defaultProps = {
  catering: false,
  orderId: undefined,
  order: undefined,
  canReorder: true,
  startEditOrder: undefined,
  handleCancelOrder: undefined,
  handleReorder: undefined,
  isCateringCutoffPassed: false,
  isOrderInactive: false,
  isAccountPage: false,
};

const ConnectedReorderModal = observer(ReorderModal);

export { ConnectedReorderModal as ReorderModal };
